import React, { useState, useEffect } from 'react';
import { 
  Server, 
  Play, 
  Square, 
  RotateCcw, 
  Trash2, 
  Filter, 
  RefreshCw,
  Tag,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { instanceService } from '../services/instanceService';
import InstanceCard from './InstanceCard';
import FilterPanel from './FilterPanel';

const InstanceList = () => {
  const [instances, setInstances] = useState([]);
  const [filteredInstances, setFilteredInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    state: '',
    environment: '',
    project: '',
    owner: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInstances, setSelectedInstances] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // 에러 메시지를 사용자 친화적으로 변환하는 함수
  const getDisplayErrorMessage = (error) => {
    // 문자열인 경우 그대로 반환
    if (typeof error === 'string') {
      return error;
    }
    
    // Error 객체인 경우
    if (error instanceof Error) {
      return error.message;
    }
    
    // 구조화된 에러 객체인 경우
    if (error && typeof error === 'object') {
      // AWS 에러의 경우 details.awsErrorMessage 우선 사용
      if (error.details?.awsErrorMessage) {
        const awsMessage = error.details.awsErrorMessage;
        
        // AWS 종료 방지 에러 한국어 변환
        if (awsMessage.includes('disableApiTermination')) {
          return '이 인스턴스는 종료 방지가 설정되어 있습니다. 인스턴스 설정에서 종료 방지를 해제한 후 다시 시도해주세요.';
        }
        
        if (awsMessage.includes('OperationNotPermitted')) {
          return '작업이 허용되지 않습니다. 인스턴스 상태 또는 권한을 확인해주세요.';
        }
        
        return awsMessage;
      }
      
      // 일반적인 에러 메시지
      if (error.message) {
        return error.message;
      }
    }
    
    return null;
  };

  useEffect(() => {
    loadInstances();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [instances, filters]);

  const loadInstances = async () => {
    try {
      setLoading(true);
      const data = await instanceService.getInstances(filters);
      setInstances(data);
      setError('');
    } catch (error) {
      console.error('인스턴스 로드 오류:', error);
      setError('인스턴스 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...instances];

    if (filters.state) {
      filtered = filtered.filter(instance => instance.state === filters.state);
    }

    if (filters.environment) {
      filtered = filtered.filter(instance => 
        instance.tags?.Environment === filters.environment
      );
    }

    if (filters.project) {
      filtered = filtered.filter(instance => 
        instance.tags?.Project === filters.project
      );
    }

    if (filters.owner) {
      filtered = filtered.filter(instance => 
        instance.tags?.Owner === filters.owner
      );
    }

    setFilteredInstances(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleInstanceAction = async (instanceId, action) => {
    try {
      await instanceService.controlInstance(instanceId, action);
      
      // 인스턴스 목록 새로고침
      setTimeout(() => {
        loadInstances();
      }, 1000);
      
      return { success: true };
    } catch (error) {
      console.error(`인스턴스 ${action} 오류:`, error);
      const errorMessage = getDisplayErrorMessage(error) || error.message || `${action} 작업에 실패했습니다.`;
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedInstances.length === 0) {
      alert('작업할 인스턴스를 선택해주세요.');
      return;
    }

    const confirmMessage = `선택한 ${selectedInstances.length}개의 인스턴스를 ${action}하시겠습니까?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setBulkActionLoading(true);

    try {
      const promises = selectedInstances.map(instanceId => 
        instanceService.controlInstance(instanceId, action)
      );

      await Promise.all(promises);
      
      setSelectedInstances([]);
      
      // 잠시 후 목록 새로고침
      setTimeout(() => {
        loadInstances();
      }, 2000);
      
      alert(`${selectedInstances.length}개 인스턴스에 대한 ${action} 작업이 요청되었습니다.`);
    } catch (error) {
      console.error('일괄 작업 오류:', error);
      alert('일부 작업이 실패했습니다. 다시 시도해주세요.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleInstanceSelect = (instanceId, selected) => {
    if (selected) {
      setSelectedInstances([...selectedInstances, instanceId]);
    } else {
      setSelectedInstances(selectedInstances.filter(id => id !== instanceId));
    }
  };

  const handleSelectAll = () => {
    if (selectedInstances.length === filteredInstances.length) {
      setSelectedInstances([]);
    } else {
      setSelectedInstances(filteredInstances.map(i => i.instanceId));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">인스턴스 목록 로딩 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">오류</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button
              onClick={loadInstances}
              className="mt-2 text-sm text-red-600 underline hover:text-red-500"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">EC2 인스턴스</h1>
          <p className="mt-1 text-sm text-gray-600">
            총 {filteredInstances.length}개의 인스턴스
          </p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary flex items-center space-x-2"
          >
            <Filter size={16} />
            <span>필터</span>
          </button>
          
          <button
            onClick={loadInstances}
            className="btn-primary flex items-center space-x-2"
          >
            <RefreshCw size={16} />
            <span>새로고침</span>
          </button>
        </div>
      </div>

      {/* 필터 패널 */}
      {showFilters && (
        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          instances={instances}
        />
      )}

      {/* 일괄 작업 패널 */}
      {selectedInstances.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-blue-700">
              {selectedInstances.length}개의 인스턴스가 선택됨
            </p>
            
            <div className="flex space-x-2">
              <button
                onClick={() => handleBulkAction('start')}
                disabled={bulkActionLoading}
                className="btn-success flex items-center space-x-1 text-sm py-1 px-3"
              >
                <Play size={14} />
                <span>시작</span>
              </button>
              
              <button
                onClick={() => handleBulkAction('stop')}
                disabled={bulkActionLoading}
                className="btn-danger flex items-center space-x-1 text-sm py-1 px-3"
              >
                <Square size={14} />
                <span>중지</span>
              </button>
              
              <button
                onClick={() => setSelectedInstances([])}
                className="btn-secondary text-sm py-1 px-3"
              >
                선택 해제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 인스턴스 목록 */}
      {filteredInstances.length === 0 ? (
        <div className="text-center py-12">
          <Server className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">인스턴스가 없습니다</h3>
          <p className="mt-1 text-sm text-gray-500">
            {instances.length === 0 
              ? 'AWS 계정에 EC2 인스턴스가 없습니다.' 
              : '필터 조건에 맞는 인스턴스가 없습니다.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* 전체 선택 체크박스 */}
          <div className="flex items-center space-x-3 pb-2 border-b border-gray-200">
            <input
              type="checkbox"
              checked={selectedInstances.length === filteredInstances.length && filteredInstances.length > 0}
              onChange={handleSelectAll}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="text-sm text-gray-700">
              전체 선택 ({filteredInstances.length}개)
            </label>
          </div>

          {/* 인스턴스 카드들 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredInstances.map((instance) => (
              <InstanceCard
                key={instance.instanceId}
                instance={instance}
                selected={selectedInstances.includes(instance.instanceId)}
                onSelect={handleInstanceSelect}
                onAction={handleInstanceAction}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InstanceList;